/**
 * MergerAnalysisResults Component
 *
 * Displays comprehensive merger analysis results including:
 * - Combined entity summary
 * - Risk assessment
 * - BASIC scores comparison
 * - Recommendations
 */

import React from 'react';
import {
  Building2,
  Users,
  Truck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  FileText,
  Shield,
  AlertCircle,
  Activity,
  BarChart3,
  Scale,
} from 'lucide-react';
import type { MergerAnalysis, RiskFactor } from '../../services/merger.api';
import BasicComparisonTable from './BasicComparisonTable';

interface MergerAnalysisResultsProps {
  analysis: MergerAnalysis;
  onExportPdf?: () => void;
  onExportCsv?: () => void;
  onExportJson?: () => void;
  isExporting?: boolean;
}

// Risk level configuration
const RISK_LEVEL_CONFIG: Record<
  string,
  { color: string; bgColor: string; icon: React.ElementType; label: string }
> = {
  LOW: {
    color: '#4b5c3a',
    bgColor: '#d1e6d9',
    icon: CheckCircle,
    label: 'Low Risk',
  },
  MEDIUM: {
    color: '#664911',
    bgColor: '#f1e2c2',
    icon: AlertCircle,
    label: 'Medium Risk',
  },
  HIGH: {
    color: '#984c26',
    bgColor: '#f6dfcf',
    icon: AlertTriangle,
    label: 'High Risk',
  },
  CRITICAL: {
    color: '#7a2b1e',
    bgColor: '#f2dad2',
    icon: XCircle,
    label: 'Critical Risk',
  },
};

// Recommendation configuration
const RECOMMENDATION_CONFIG: Record<
  string,
  { color: string; bgColor: string; icon: React.ElementType; label: string }
> = {
  APPROVE: {
    color: '#4b5c3a',
    bgColor: '#d1e6d9',
    icon: CheckCircle,
    label: 'Approved',
  },
  REVIEW: {
    color: '#664911',
    bgColor: '#f1e2c2',
    icon: AlertCircle,
    label: 'Review Required',
  },
  REJECT: {
    color: '#7a2b1e',
    bgColor: '#f2dad2',
    icon: XCircle,
    label: 'Not Recommended',
  },
};

// Risk factor badge component
const RiskFactorBadge: React.FC<{ factor: RiskFactor }> = ({ factor }) => {
  const impactConfig: Record<string, { color: string; bgColor: string }> = {
    LOW: { color: '#4b5c3a', bgColor: '#d1e6d9' },
    MEDIUM: { color: '#664911', bgColor: '#f1e2c2' },
    HIGH: { color: '#984c26', bgColor: '#f6dfcf' },
    CRITICAL: { color: '#7a2b1e', bgColor: '#f2dad2' },
  };

  const config = impactConfig[factor.impact] || impactConfig.LOW;

  return (
    <div
      style={{
        padding: 'var(--space-md)',
        backgroundColor: 'var(--color-gray-50)',
        borderRadius: 'var(--radius-md)',
        borderLeft: `4px solid ${config.color}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-xs)',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            color: 'var(--color-gray-900)',
          }}
        >
          {factor.category}
        </span>
        <span
          style={{
            fontSize: '0.75rem',
            padding: '0.125rem 0.5rem',
            borderRadius: 'var(--radius-sm)',
            backgroundColor: config.bgColor,
            color: config.color,
            fontWeight: 500,
          }}
        >
          {factor.impact}
        </span>
      </div>
      <p
        style={{
          fontSize: '0.875rem',
          color: 'var(--color-gray-600)',
          margin: 0,
        }}
      >
        {factor.description}
      </p>
    </div>
  );
};

// Stat card component
const StatCard: React.FC<{
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  color?: string;
}> = ({ icon: Icon, label, value, subValue, color = 'var(--color-primary)' }) => (
  <div
    style={{
      padding: 'var(--space-md)',
      backgroundColor: '#fbf9f4',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--color-gray-200)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        marginBottom: 'var(--space-sm)',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 18, height: 18, color: color }} />
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
      {value}
    </div>
    {subValue && (
      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', marginTop: 'var(--space-xs)' }}>
        {subValue}
      </div>
    )}
  </div>
);

const MergerAnalysisResults: React.FC<MergerAnalysisResultsProps> = ({
  analysis,
  onExportPdf,
  onExportCsv,
  onExportJson,
  isExporting = false,
}) => {
  const { carriers, combined, comparison, riskAssessment } = analysis;

  const riskConfig = RISK_LEVEL_CONFIG[riskAssessment.level] || RISK_LEVEL_CONFIG.MEDIUM;
  const recommendationConfig =
    RECOMMENDATION_CONFIG[riskAssessment.recommendation] || RECOMMENDATION_CONFIG.REVIEW;

  const RiskIcon = riskConfig.icon;
  const RecommendationIcon = recommendationConfig.icon;

  return (
    <div className="fade-in-up">
      {/* Header */}
      <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <h2
            className="heading-3"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-primary)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              2
            </span>
            Merger Analysis Results
          </h2>
          <p className="text-muted" style={{ marginTop: 'var(--space-xs)' }}>
            Analysis completed on{' '}
            {new Date(analysis.calculatedAt).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>

        {/* Risk Assessment Summary */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-lg)',
          }}
        >
          {/* Risk Score */}
          <div
            style={{
              padding: 'var(--space-lg)',
              backgroundColor: riskConfig.bgColor,
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${riskConfig.color}20`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-md)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: '#fbf9f4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <RiskIcon style={{ width: 32, height: 32, color: riskConfig.color }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: riskConfig.color,
                    marginBottom: 'var(--space-xs)',
                  }}
                >
                  Risk Assessment
                </div>
                <div
                  style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: riskConfig.color,
                    lineHeight: 1,
                  }}
                >
                  {riskAssessment.score}/100
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: riskConfig.color,
                    marginTop: 'var(--space-xs)',
                  }}
                >
                  {riskConfig.label}
                </div>
              </div>
            </div>
          </div>

          {/* Recommendation */}
          <div
            style={{
              padding: 'var(--space-lg)',
              backgroundColor: recommendationConfig.bgColor,
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${recommendationConfig.color}20`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-md)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: '#fbf9f4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <RecommendationIcon
                  style={{ width: 32, height: 32, color: recommendationConfig.color }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: recommendationConfig.color,
                    marginBottom: 'var(--space-xs)',
                  }}
                >
                  Recommendation
                </div>
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: recommendationConfig.color,
                    lineHeight: 1,
                  }}
                >
                  {recommendationConfig.label}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rationale */}
        {riskAssessment.rationale && (
          <div
            style={{
              padding: 'var(--space-md)',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-md)',
              borderLeft: '4px solid var(--color-primary)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-md)',
              }}
            >
              <FileText
                style={{
                  width: 20,
                  height: 20,
                  color: 'var(--color-primary)',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    color: 'var(--color-gray-900)',
                    marginBottom: 'var(--space-xs)',
                  }}
                >
                  Analysis Summary
                </div>
                <p
                  style={{
                    fontSize: '0.9375rem',
                    color: 'var(--color-gray-700)',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {riskAssessment.rationale}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Combined Entity Profile */}
      <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3
          className="heading-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-lg)',
          }}
        >
          <Building2 style={{ width: 20, height: 20, color: 'var(--color-primary)' }} />
          Combined Entity Profile
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--space-md)',
          }}
        >
          <StatCard
            icon={Truck}
            label="Power Units"
            value={combined.totalFleet.powerUnits.toLocaleString()}
            subValue="Combined fleet size"
            color="var(--color-primary)"
          />
          <StatCard
            icon={Users}
            label="Drivers"
            value={combined.totalFleet.drivers.toLocaleString()}
            subValue="Total workforce"
            color="#6d685e"
          />
          <StatCard
            icon={Activity}
            label="Inspections"
            value={combined.totalInspections.toLocaleString()}
            subValue="Past 24 months"
            color="#4a463f"
          />
          <StatCard
            icon={AlertTriangle}
            label="Violations"
            value={combined.totalViolations.toLocaleString()}
            subValue="Past 24 months"
            color="#b07d2b"
          />
          <StatCard
            icon={BarChart3}
            label="Crashes"
            value={combined.totalCrashes}
            subValue={`${combined.fatalCrashes} fatal`}
            color="#b3412c"
          />
          <StatCard
            icon={Scale}
            label="Peer Group"
            value={combined.peerGroup || 'Combination'}
            subValue="Carrier classification"
            color="#5d7148"
          />
        </div>
      </div>

      {/* BASIC Scores Comparison */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <BasicComparisonTable
          carriers={carriers}
          combinedBasics={combined.combinedBasics}
          showIndividualScores={carriers.length <= 5}
        />
      </div>

      {/* Comparison Summary */}
      <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
        <h3
          className="heading-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-lg)',
          }}
        >
          <BarChart3 style={{ width: 20, height: 20, color: 'var(--color-primary)' }} />
          Before vs After Comparison
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 'var(--space-lg)',
          }}
        >
          {/* Before Stats */}
          <div
            style={{
              padding: 'var(--space-md)',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-gray-500)',
                marginBottom: 'var(--space-md)',
              }}
            >
              Before Merger (Average)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Avg BASIC Percentile</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.beforeMerger.avgPercentile.toFixed(1)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Above Threshold</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.beforeMerger.scoresAboveThreshold} categories
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Total Violations</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.beforeMerger.totalViolations.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* After Stats */}
          <div
            style={{
              padding: 'var(--space-md)',
              backgroundColor: 'var(--color-primary)',
              borderRadius: 'var(--radius-md)',
              color: 'white',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                opacity: 0.8,
                marginBottom: 'var(--space-md)',
              }}
            >
              After Merger (Combined)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.9 }}>Avg BASIC Percentile</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.afterMerger.avgPercentile.toFixed(1)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.9 }}>Above Threshold</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.afterMerger.scoresAboveThreshold} categories
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.9 }}>Total Violations</span>
                <span style={{ fontWeight: 600 }}>
                  {comparison.afterMerger.totalViolations.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Improvement/Deterioration */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 'var(--space-md)',
            marginTop: 'var(--space-lg)',
          }}
        >
          {comparison.improvement.categories.length > 0 && (
            <div
              style={{
                padding: 'var(--space-md)',
                backgroundColor: '#d1e6d9',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  marginBottom: 'var(--space-sm)',
                }}
              >
                <TrendingDown style={{ width: 16, height: 16, color: '#4b5c3a' }} />
                <span style={{ fontWeight: 600, color: '#4b5c3a' }}>
                  Improved Categories ({comparison.improvement.categories.length})
                </span>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#4b5c3a', margin: 0 }}>
                {comparison.improvement.categories.join(', ')}
              </p>
            </div>
          )}

          {comparison.deterioration.categories.length > 0 && (
            <div
              style={{
                padding: 'var(--space-md)',
                backgroundColor: '#f2dad2',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  marginBottom: 'var(--space-sm)',
                }}
              >
                <TrendingUp style={{ width: 16, height: 16, color: '#7a2b1e' }} />
                <span style={{ fontWeight: 600, color: '#7a2b1e' }}>
                  Worsened Categories ({comparison.deterioration.categories.length})
                </span>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#7a2b1e', margin: 0 }}>
                {comparison.deterioration.categories.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Risk Factors */}
      {riskAssessment.factors.length > 0 && (
        <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3
            className="heading-3"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)',
            }}
          >
            <Shield style={{ width: 20, height: 20, color: 'var(--color-primary)' }} />
            Risk Factors
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {riskAssessment.factors.map((factor, index) => (
              <RiskFactorBadge key={index} factor={factor} />
            ))}
          </div>
        </div>
      )}

      {/* Compliance Issues */}
      {riskAssessment.complianceIssues.length > 0 && (
        <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3
            className="heading-3"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              marginBottom: 'var(--space-lg)',
            }}
          >
            <AlertTriangle style={{ width: 20, height: 20, color: '#b07d2b' }} />
            Compliance Issues ({riskAssessment.complianceIssues.length})
          </h3>

          <ul
            style={{
              margin: 0,
              paddingLeft: 'var(--space-lg)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-sm)',
            }}
          >
            {riskAssessment.complianceIssues.map((issue, index) => (
              <li
                key={index}
                style={{
                  color: 'var(--color-gray-700)',
                  fontSize: '0.9375rem',
                }}
              >
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Export Buttons */}
      <div className="card-modern">
        <h3
          className="heading-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-lg)',
          }}
        >
          <FileText style={{ width: 20, height: 20, color: 'var(--color-primary)' }} />
          Export Analysis
        </h3>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-md)',
          }}
        >
          <button
            onClick={onExportPdf}
            disabled={isExporting || !onExportPdf}
            className="btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              opacity: isExporting || !onExportPdf ? 0.5 : 1,
            }}
          >
            <FileText style={{ width: 18, height: 18 }} />
            Export PDF Report
          </button>

          <button
            onClick={onExportCsv}
            disabled={isExporting || !onExportCsv}
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              opacity: isExporting || !onExportCsv ? 0.5 : 1,
            }}
          >
            <BarChart3 style={{ width: 18, height: 18 }} />
            Export CSV Data
          </button>

          <button
            onClick={onExportJson}
            disabled={isExporting || !onExportJson}
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              opacity: isExporting || !onExportJson ? 0.5 : 1,
            }}
          >
            <FileText style={{ width: 18, height: 18 }} />
            Export JSON
          </button>
        </div>

        <p
          className="text-muted"
          style={{ marginTop: 'var(--space-md)', fontSize: '0.8125rem' }}
        >
          Export this analysis for your records or share with stakeholders.
        </p>
      </div>
    </div>
  );
};

export default MergerAnalysisResults;
